import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SERVICES = [
  { name: "Ocean Freight", desc: "Full container load (FCL) and less than container load (LCL) shipping for maritime cargo." },
  { name: "Land Transportation", desc: "Door-to-door delivery with reliable trucking and rail freight across continents." },
  { name: "Warehousing", desc: "Storage facilities with inventory management and distribution services." },
  { name: "Global Logistics", desc: "End-to-end supply chain solutions connecting businesses to markets worldwide." },
  { name: "Port Services", desc: "Port handling, customs clearance and documentation services." },
  { name: "Container Services", desc: "Container leasing, maintenance and specialized container solutions." },
];

const IMG_AERIAL =
  "https://images.pexels.com/photos/14810115/pexels-photo-14810115.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_SHIP_NIGHT =
  "https://images.pexels.com/photos/17835642/pexels-photo-17835642.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_PORT_NIGHT =
  "https://images.pexels.com/photos/20216716/pexels-photo-20216716.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_VESSEL =
  "https://images.unsplash.com/photo-1605745341112-85968b19335b?w=1600&q=80";

const SPECS = [
  { value: 178, suffix: " / 29", label: "Dimensions — m" },
  { value: 22800, suffix: "", label: "Gross Tonnage — t" },
  { value: 37000, suffix: "", label: "Summer DWT — t" },
];

const fmt = (v) => Math.round(v).toLocaleString("en-US");

export default function Logistics() {
  const root = useRef(null);
  const trackRef = useRef(null);
  const numRefs = useRef([]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      const track = trackRef.current;
      const tween = gsap.to(track, {
        x: () => -(track.scrollWidth - window.innerWidth),
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: () => `+=${track.scrollWidth - window.innerWidth}`,
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      gsap.utils.toArray(".logi-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { clipPath: "inset(0 100% 0 0)" },
          {
            clipPath: "inset(0 0% 0 0)",
            ease: "none",
            scrollTrigger: {
              trigger: el,
              containerAnimation: tween,
              start: "left 85%",
              end: "left 35%",
              scrub: true,
            },
          }
        );
      });

      gsap.utils.toArray(".logi-parallax").forEach((img) => {
        gsap.fromTo(
          img,
          { xPercent: -7 },
          {
            xPercent: 7,
            ease: "none",
            scrollTrigger: {
              trigger: img,
              containerAnimation: tween,
              start: "left right",
              end: "right left",
              scrub: true,
            },
          }
        );
      });

      gsap.fromTo(
        ".srv-row",
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".srv-list",
            containerAnimation: tween,
            start: "left 80%",
          },
        }
      );

      /* Fleet emerges at the end of the logistics journey */
      const counters = SPECS.map(() => ({ v: 0 }));
      SPECS.forEach((s, i) => {
        gsap.to(counters[i], {
          v: s.value,
          ease: "none",
          onUpdate: () => {
            const el = numRefs.current[i];
            if (el) el.textContent = fmt(counters[i].v) + s.suffix;
          },
          scrollTrigger: {
            trigger: ".hfleet-panel",
            containerAnimation: tween,
            start: "left 75%",
            end: "left 25%",
            scrub: true,
          },
        });
      });
      gsap.fromTo(
        ".hfleet-panel .fleet-img",
        { clipPath: "inset(0 100% 0 0)" },
        {
          clipPath: "inset(0 0% 0 0)",
          ease: "none",
          scrollTrigger: {
            trigger: ".hfleet-panel .fleet-img",
            containerAnimation: tween,
            start: "left 95%",
            end: "left 40%",
            scrub: true,
          },
        }
      );
      gsap.fromTo(
        ".hfleet-panel .fleet-img-inner",
        { xPercent: -7 },
        {
          xPercent: 7,
          ease: "none",
          scrollTrigger: {
            trigger: ".hfleet-panel .fleet-img",
            containerAnimation: tween,
            start: "left right",
            end: "right left",
            scrub: true,
          },
        }
      );
      gsap.fromTo(
        ".hfleet-panel .fleet-line",
        { y: 0, yPercent: 120 },
        {
          y: 0,
          yPercent: 0,
          stagger: 0.1,
          duration: 0.5,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".hfleet-panel",
            containerAnimation: tween,
            start: "left 80%",
          },
        }
      );
      gsap.fromTo(
        ".hfleet-panel .fleet-overline, .hfleet-panel .fleet-copy, .hfleet-panel .fleet-spec",
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          duration: 0.5,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".hfleet-panel",
            containerAnimation: tween,
            start: "left 70%",
          },
        }
      );
      gsap.fromTo(
        ".hfleet-panel .fleet-rule",
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.6,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: ".hfleet-panel",
            containerAnimation: tween,
            start: "left 70%",
          },
        }
      );

      window.__anchors = {
        ...(window.__anchors || {}),
        "#fleet": () => {
          const panel = track.querySelector(".hfleet-panel");
          return tween.scrollTrigger.start + panel.offsetLeft + window.innerWidth * 0.1;
        },
      };
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      id="logistics"
      className="relative overflow-hidden"
      data-testid="logistics-scene"
      aria-label="Logistics capabilities"
    >
      <div ref={trackRef} className="logi-track flex h-[100svh] w-max">
        {/* Panel 1 — chapter opener */}
        <div className="logi-panel relative h-full w-[92vw] shrink-0">
          <div className="relative z-10 flex h-full flex-col justify-center px-[6vw]">
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
              03 — Logistics
            </p>
            <h2 className="mt-6 font-display text-[14vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[10vw]">
              Logistics
            </h2>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-smoke md:text-base">
              A one-stop-shop for shipping — ocean freight, land transportation,
              warehousing, global logistics, port and container services. Moving
              cargo worldwide since 1999.
            </p>
          </div>
          <div className="logi-reveal absolute right-[5vw] top-1/2 hidden h-[64vh] w-[32vw] -translate-y-1/2 overflow-hidden md:block">
            <img
              src={IMG_AERIAL}
              alt="Aerial view of a cargo ship crossing deep ocean water"
              className="logi-parallax h-full w-[115%] max-w-none object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>

        {/* Panel 2 — ocean freight */}
        <div className="logi-panel relative h-full w-[100vw] shrink-0 md:w-[115vw]">
          <div className="logi-reveal absolute left-0 top-0 h-full w-full overflow-hidden md:w-[62vw]">
            <img
              src={IMG_SHIP_NIGHT}
              alt="Container ship illuminated at night at sea"
              className="logi-parallax h-full w-[115%] max-w-none object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent md:bg-gradient-to-r md:from-black/30 md:via-transparent md:to-ink" />
          </div>
          <span className="text-outline-faint pointer-events-none absolute right-[4vw] top-[8vh] font-display text-[24vw] font-black leading-none md:text-[18vw]">
            01
          </span>
          <div className="absolute bottom-[12vh] left-[6vw] max-w-md md:left-auto md:right-[6vw] md:top-1/2 md:-translate-y-1/2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">
              Capability 01 / 06
            </p>
            <h3 className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter md:text-[4.5vw]">
              Ocean Freight
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-smoke md:text-base">
              Full container load and less than container load shipping solutions
              for maritime cargo — worldwide.
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              FCL · LCL · Global Routes
            </p>
          </div>
        </div>

        {/* Panel 3 — land & port */}
        <div className="logi-panel relative h-full w-[100vw] shrink-0 md:w-[115vw]">
          <div className="logi-reveal absolute right-0 top-0 h-full w-full overflow-hidden md:w-[62vw]">
            <img
              src={IMG_PORT_NIGHT}
              alt="Industrial port terminal illuminated at night"
              className="logi-parallax h-full w-[115%] max-w-none object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-black/60 via-black/20 to-transparent md:bg-gradient-to-l md:from-black/30 md:via-transparent md:to-ink" />
          </div>
          <span className="text-outline-faint pointer-events-none absolute left-[4vw] top-[8vh] font-display text-[24vw] font-black leading-none md:text-[18vw]">
            02
          </span>
          <div className="absolute bottom-[12vh] left-[6vw] max-w-md md:top-1/2 md:-translate-y-1/2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">
              Capabilities 02 + 05 / 06
            </p>
            <h3 className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter md:text-[4.5vw]">
              Port to Door
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-smoke md:text-base">
              Door-to-door trucking and rail freight across continents — with
              comprehensive port handling, customs clearance and documentation.
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              Trucking · Rail · Customs Clearance
            </p>
          </div>
        </div>

        {/* Panel 4 — capability index */}
        <div className="logi-panel relative flex h-full w-[100vw] shrink-0 items-center px-[6vw] md:w-[105vw]">
          <div className="srv-list w-full">
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
              Full Capability Index
            </p>
            <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
              {SERVICES.map((s, i) => (
                <div
                  key={s.name}
                  className="srv-row group flex items-baseline justify-between gap-6 py-4 md:py-5"
                  data-testid={`service-row-${i}`}
                >
                  <div className="flex items-baseline gap-5 md:gap-8">
                    <span className="font-mono text-xs text-smoke">0{i + 1}</span>
                    <h4 className="font-display text-2xl font-bold uppercase tracking-tight transition-colors duration-300 group-hover:text-amber md:text-4xl">
                      {s.name}
                    </h4>
                  </div>
                  <p className="hidden max-w-xs text-right text-xs leading-relaxed text-smoke lg:block">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Panel 5 — scale */}
        <div className="logi-panel relative flex h-full w-[85vw] shrink-0 flex-col justify-center px-[6vw]">
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-smoke md:text-xs">
            A Network in Motion
          </p>
          <div
            className="mt-4 font-display text-[34vw] font-black leading-none tracking-tighter text-amber md:text-[20vw]"
            data-testid="logistics-stat-countries"
          >
            100<span className="text-bone">+</span>
          </div>
          <p className="mt-2 font-display text-2xl font-bold uppercase tracking-tight md:text-4xl">
            Countries
          </p>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-smoke md:text-base">
            A vast network of partners and agents enabling seamless shipping
            services across the globe.
          </p>
        </div>

        {/* Panel 6 — the fleet emerges from the logistics flow */}
        <div
          className="hfleet-panel logi-panel relative h-full w-[100vw] shrink-0 md:w-[130vw]"
          data-testid="fleet-panel"
        >
          <div className="fleet-img absolute left-[6vw] top-[12vh] h-[30vh] w-[88vw] overflow-hidden md:top-1/2 md:h-[70vh] md:w-[50vw] md:-translate-y-1/2">
            <img
              src={IMG_VESSEL}
              alt="M.V. Artax cargo vessel at sea"
              className="fleet-img-inner h-[120%] w-full object-cover brightness-[0.8] contrast-[1.05]"
              loading="lazy"
              decoding="async"
              data-testid="fleet-image"
            />
          </div>
          <div className="absolute bottom-[6vh] left-[6vw] right-[6vw] md:bottom-auto md:left-auto md:right-[5vw] md:top-1/2 md:w-[40vw] md:-translate-y-1/2">
            <p className="fleet-overline font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
              Our Fleet — Vessel Carriers
            </p>
            <div className="mt-4 overflow-hidden">
              <span className="fleet-line text-outline-faint block font-display text-[9vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[4.5vw]">
                M.V.
              </span>
            </div>
            <div className="overflow-hidden">
              <span
                className="fleet-line block font-display text-[14vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[7vw]"
                data-testid="fleet-name"
              >
                Artax
              </span>
            </div>
            <p className="fleet-copy mt-5 max-w-md text-sm leading-relaxed text-smoke md:text-base">
              One of the vessels in our fleet — 178 / 29 m, gross tonnage
              22,800 tons and summer DWT 37,000 tons.
            </p>
            <div className="fleet-rule mt-8 h-px w-full origin-left bg-white/15" />
            <div className="mt-6 grid grid-cols-3 gap-3 md:gap-6">
              {SPECS.map((s, i) => (
                <div key={s.label} className="fleet-spec" data-testid={`fleet-spec-${i}`}>
                  <div className="font-display text-xl font-black tracking-tight text-bone md:text-4xl">
                    <span ref={(el) => { if (el) numRefs.current[i] = el; }}>
                      {fmt(s.value) + s.suffix}
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-[8px] uppercase tracking-[0.2em] text-amber md:text-[10px]">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
